import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Mail, Trash2, Plus, Bell, AlertCircle } from 'lucide-react';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';

const IncidentReportSettings = () => {
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newRecipient, setNewRecipient] = useState({ name: '', email: '' });

  useEffect(() => {
    loadRecipients();
  }, []);

  const loadRecipients = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/company/incident-report-recipients');
      setRecipients(response.data.recipients || []);
    } catch (error) {
      console.error('Error loading recipients:', error);
      toast.error('Failed to load incident report recipients');
    } finally {
      setLoading(false);
    }
  };

  const saveRecipients = async (updatedRecipients) => {
    setSaving(true);
    try {
      await axiosInstance.put('/company/incident-report-recipients', {
        recipients: updatedRecipients
      });
      setRecipients(updatedRecipients);
      toast.success('Incident report recipients updated');
    } catch (error) {
      console.error('Error saving recipients:', error);
      toast.error('Failed to update recipients');
    } finally {
      setSaving(false);
    }
  };

  const handleAddRecipient = async () => {
    if (!newRecipient.name || !newRecipient.email) {
      toast.error('Please enter both name and email');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newRecipient.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Check for duplicate email
    if (recipients.some(r => r.email === newRecipient.email)) {
      toast.error('This email is already in the recipient list');
      return;
    }

    const updatedRecipients = [...recipients, newRecipient];
    await saveRecipients(updatedRecipients);
    setNewRecipient({ name: '', email: '' });
    setShowAddDialog(false);
  };

  const handleRemoveRecipient = async (email) => {
    const updatedRecipients = recipients.filter(r => r.email !== email);
    await saveRecipients(updatedRecipients);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-center text-gray-500">Loading settings...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center">
                <Bell className="w-5 h-5 mr-2 text-orange-600" />
                Incident Report Notifications
              </CardTitle>
              <CardDescription>
                Configure who receives email alerts when incident reports are submitted
              </CardDescription>
            </div>
            <Button onClick={() => setShowAddDialog(true)} size="sm">
              <Plus className="w-4 h-4 mr-1" />
              Add Recipient
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
              <div>
                <p className="text-sm text-blue-900 font-semibold">How It Works</p>
                <p className="text-sm text-blue-800 mt-1">
                  When a supervisor submits an incident report, automatic email notifications will be sent to 
                  all configured recipients. Add managers, safety officers, or anyone who needs to be notified 
                  of workplace incidents.
                </p>
              </div>
            </div>
          </div>

          {recipients.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <Mail className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 font-medium mb-2">No Recipients Configured</p>
              <p className="text-sm text-gray-500 mb-4">
                By default, all company admins will receive notifications.<br />
                Add specific recipients to customize who gets notified.
              </p>
              <Button onClick={() => setShowAddDialog(true)} variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Add First Recipient
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {recipients.map((recipient, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="bg-orange-100 p-2 rounded-full">
                      <Mail className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{recipient.name}</p>
                      <p className="text-sm text-gray-600">{recipient.email}</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleRemoveRecipient(recipient.email)}
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    disabled={saving}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {recipients.length > 0 && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-800">
                <strong>Note:</strong> All {recipients.length} recipient{recipients.length !== 1 ? 's' : ''} will 
                receive an email notification whenever an incident report is submitted. Ensure email addresses 
                are correct and monitored regularly.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Recipient Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Incident Report Recipient</DialogTitle>
            <DialogDescription>
              Add someone who should receive email notifications when incident reports are submitted
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="recipient-name">Name</Label>
              <Input
                id="recipient-name"
                value={newRecipient.name}
                onChange={(e) => setNewRecipient({ ...newRecipient, name: e.target.value })}
                placeholder="e.g., John Smith"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="recipient-email">Email Address</Label>
              <Input
                id="recipient-email"
                type="email"
                value={newRecipient.email}
                onChange={(e) => setNewRecipient({ ...newRecipient, email: e.target.value })}
                placeholder="e.g., john.smith@company.com"
                className="mt-1"
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-800">
                <strong>💡 Tip:</strong> Add safety managers, HR personnel, or anyone responsible for 
                incident response. They'll receive immediate notifications when incidents are reported.
              </p>
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                onClick={() => {
                  setShowAddDialog(false);
                  setNewRecipient({ name: '', email: '' });
                }}
                variant="outline"
                className="flex-1"
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddRecipient}
                className="flex-1"
                disabled={saving || !newRecipient.name || !newRecipient.email}
              >
                {saving ? 'Adding...' : 'Add Recipient'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default IncidentReportSettings;
